/**
 * 구독 관리 (261011-subscription-billing-payup).
 *
 *  - `BillingPage`        /app/billing — 내 브랜드들의 제안을 **내 카드**로 시작하고, 진행 중 구독의 카드를 바꾼다.
 *                         카드는 계정 것이라 소속 브랜드 여럿에 한 카드를 쓴다 (261013-account-brand-membership)
 *  - `PaymentPending`     결제 대기(confirmed) 고객의 첫 화면 — 셸 없이 구독 시작만(다른 계약은 서버가 403)
 *  - `AdminBillingPage`   운영자 /admin/billing — 확인 필요 결제·구독 전체·해지·환불
 *  - `BrandBillingAdmin` 운영자 고객 상세 — 그 고객의 구독과 제안
 *
 * ★ 해지·결제 취소·환불은 **운영자만** 한다. 결제자 화면에는 그 버튼이 없고 「담당자에게 문의」만 안내한다.
 * ★ 금액·날짜는 서버가 계산한 값만 그린다(`price`·`nextChargeOn`). 화면이 VAT·구독일을 계산하지 않는다.
 * ★ 카드 입력 칸은 `autoComplete="off"` 이고 값은 이 화면 상태로만 산다 — 보낸 뒤 바로 지운다.
 */
import { auth, billing, registry } from '@geo-admin/shared/contract/api/index.ts';
import { DEFAULT_SUBSCRIPTION_NAME, SUBSCRIPTION_TERMS_VERSION } from '@geo-admin/shared/contract/api/billing.ts';
import type {
  AdminCharge,
  AdminChargeListItem,
  AdminSubscription,
  Card as BillingCard,
  PayerCharge,
  PayerSubscription,
} from '@geo-admin/shared/contract/api/billing.ts';
import type { Me } from '@geo-admin/shared/contract/api/auth.ts';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { BLANK_CARD, CardFields, cardInputOf } from '../components/CardFields.tsx';
import { ImpersonationBanner } from '../components/ImpersonationBanner.tsx';
import { PageTitle } from '../components/PageTitle.tsx';
import { BrandKindTabs, useBrandKind } from '../components/BrandKindTabs.tsx';
import { SupportContact } from '../components/SupportContact.tsx';
import { TermsSummary } from '../components/TermsSummary.tsx';
import { Button } from '../components/ui/button.tsx';
import { Badge, Card, CardBody, CardHeader } from '../components/ui/card.tsx';
import { Dialog } from '../components/ui/dialog.tsx';
import { FieldError, Input, Label, Select } from '../components/ui/form.tsx';
import { Empty, ErrorBox, Loading } from '../components/ui/states.tsx';
import { useApi, useApiMutation } from '../lib/api.ts';
import { call } from '../lib/call.ts';
import { dateTime } from '../lib/format.ts';
import { pathsFor } from '../lib/paths.ts';
import { cn } from '../lib/utils.ts';
import { SUBSCRIPTION_SERVICES } from '../legal/content/subscription.ts';

const won = (n: number) => `${n.toLocaleString('ko-KR')}원`;

const STATUS: Record<
  PayerSubscription['status'],
  { label: string; tone: 'slate' | 'green' | 'amber' | 'red' | 'blue' }
> = {
  offered: { label: '시작 전', tone: 'blue' },
  active: { label: '이용 중', tone: 'green' },
  past_due: { label: '결제 필요', tone: 'red' },
  canceled: { label: '종료', tone: 'slate' },
};

function TestModeBanner({ on }: { on: boolean }) {
  if (!on) return null;
  return (
    <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900 text-sm">
      테스트 결제 환경입니다 — 실제 카드 대금이 청구되지 않습니다.
    </div>
  );
}

/** 결제 카드 등록 — 페이업으로 바로 보내고, 우리 서버에는 끝 4자리만 남는다 */
function CardDialog({ open, onClose }: { open: boolean; onClose: (cardId?: string) => void }) {
  const register = useApiMutation(billing.registerCard);
  const [f, setF] = useState(BLANK_CARD);
  const close = (id?: string) => {
    setF(BLANK_CARD);
    register.reset();
    onClose(id);
  };
  return (
    <Dialog open={open} onClose={() => close()} title="결제 카드 등록">
      <form
        className="grid gap-3"
        autoComplete="off"
        onSubmit={(e) => {
          e.preventDefault();
          register.mutate({ body: { card: cardInputOf(f) } }, { onSuccess: (r) => close(r.card.id) });
        }}
      >
        <p className="text-slate-600 text-sm">
          내 계정의 카드로 등록됩니다 — 내가 관리하는 브랜드 어느 곳의 구독에도 쓸 수 있습니다.
        </p>
        <CardFields value={f} onChange={setF} />
        <FieldError>{register.error?.message}</FieldError>
        <Button type="submit" variant="live" disabled={register.isPending}>
          {register.isPending ? '확인하는 중…' : '카드 등록'}
        </Button>
        <p className="text-slate-500 text-xs">
          카드 정보는 결제대행사(페이업)로 바로 전달되고 저장하지 않습니다. 카드번호는 끝 4자리만 보관합니다.
        </p>
      </form>
    </Dialog>
  );
}

/** 이 구독에 고를 수 있는 카드 — 내 카드 전부다(카드는 계정 것이고, 이 구독의 브랜드에 내가 소속돼 있다) */
const cardsFor = (cards: BillingCard[], _sub: PayerSubscription) => cards;

/** 저장된 카드 표시 — 끝 4자리만 안다(카드번호는 저장하지 않는다). 예: 신한카드 ****-****-****-1111 */
const cardLabel = (c: { cardName: string; last4: string }) => `${c.cardName} ****-****-****-${c.last4}`;

/** 날짜 표시 — 서버가 준 `YYYY-MM-DD` 를 점으로만 바꾼다(화면이 날짜를 계산하지 않는다) */
const dot = (d: string) => d.replaceAll('-', '.');

const PILL_TONE = {
  slate: 'bg-slate-100 text-slate-600 ring-slate-200',
  green: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  amber: 'bg-amber-50 text-amber-700 ring-amber-200',
  red: 'bg-red-50 text-red-700 ring-red-200',
  blue: 'bg-blue-50 text-blue-700 ring-blue-200',
} as const;
const DOT_TONE = {
  slate: 'bg-slate-400',
  green: 'bg-emerald-500',
  amber: 'bg-amber-500',
  red: 'bg-red-500',
  blue: 'bg-blue-500',
} as const;

/** 상태 알약 — 점 하나와 짧은 말 */
function Pill({ tone, children }: { tone: keyof typeof PILL_TONE; children: React.ReactNode }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 font-medium text-xs ring-1 ring-inset',
        PILL_TONE[tone],
      )}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full', DOT_TONE[tone])} />
      {children}
    </span>
  );
}

/** 구독 카드의 머리 — 브랜드 · 무엇을 사는가(질문이 이 구독의 대가다, 261013-subscription-query-link) · 상태 */
function PlanHeader({ sub, pills }: { sub: PayerSubscription; pills: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="grid min-w-0 gap-1">
        <div className="flex flex-wrap items-center gap-x-2 text-slate-500 text-xs">
          <span className="font-semibold text-slate-700">{sub.brandName}</span>
          <span aria-hidden>·</span>
          <span>{sub.name}</span>
        </div>
        {sub.query ? (
          <div className="font-semibold text-[17px] text-slate-900 leading-snug">“{sub.query.query}”</div>
        ) : (
          <div className="font-semibold text-[17px] text-slate-900 leading-snug">{sub.name}</div>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5">{pills}</div>
    </div>
  );
}

/** 금액 — 서버가 계산한 값만 그린다(`price`) */
function PlanPrice({ sub }: { sub: PayerSubscription }) {
  const free = sub.price.amountKrw === 0;
  return (
    <div className="grid gap-1">
      <div className="flex items-baseline gap-1.5 tabular-nums">
        <span className="font-bold text-[32px] text-slate-900 leading-none tracking-tight">
          {free ? '무료' : won(sub.price.amountKrw)}
        </span>
        {free ? null : <span className="text-slate-500 text-sm">/ 월</span>}
      </div>
      {free ? null : (
        <div className="text-slate-500 text-xs tabular-nums">
          공급가 {won(sub.price.supplyKrw)} + 부가세 {won(sub.price.vatKrw)}
        </div>
      )}
    </div>
  );
}

/** 사실 한 칸 — 이름과 값 */
function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid gap-1 rounded-xl bg-slate-50 px-4 py-3 ring-1 ring-slate-100 ring-inset">
      <div className="text-slate-500 text-xs">{label}</div>
      <div className="font-semibold text-slate-900 text-sm tabular-nums">{children}</div>
    </div>
  );
}

/** 서비스 구성 — 약관 [서비스 명세]의 요약. 문안은 `SUBSCRIPTION_SERVICES` 한 곳이다 */
const SERVICES_ON_SCREEN = [...SUBSCRIPTION_SERVICES].sort((a, b) => a.order - b.order);

function ServiceSpec() {
  return (
    <div className="grid gap-2.5">
      <div className="flex items-baseline justify-between gap-2">
        <div className="font-semibold text-slate-900 text-sm">포함 서비스</div>
        <div className="text-slate-400 text-xs">매월 제공 · 구독 기간 기준</div>
      </div>
      <ul className="grid overflow-hidden rounded-xl ring-1 ring-slate-100 ring-inset sm:grid-cols-2">
        {SERVICES_ON_SCREEN.map((v, i) => (
          <li
            key={v.name}
            className={cn(
              'flex items-center gap-3 border-slate-100 px-4 py-3',
              i > 0 && 'border-t',
              i === 1 && 'sm:border-t-0',
              i % 2 === 1 && 'sm:border-l',
              i === SERVICES_ON_SCREEN.length - 1 && i % 2 === 0 && 'sm:col-span-2',
            )}
          >
            <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-live/10 font-semibold text-live text-xs tabular-nums">
              {i + 1}
            </span>
            <div className="grid min-w-0 flex-1 gap-0.5">
              <div className="font-semibold text-slate-900 text-sm">{v.name}</div>
              <div className="text-slate-500 text-xs leading-snug">{v.brief}</div>
            </div>
            <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-1 font-semibold text-slate-700 text-xs tabular-nums">
              {v.short}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** 알림 한 줄 — 카드 안에서 할 일·문제를 눈에 띄게 */
function Callout({ tone, children }: { tone: 'amber' | 'red' | 'blue'; children: React.ReactNode }) {
  const t = {
    amber: 'border-amber-200 bg-amber-50 text-amber-900',
    red: 'border-red-200 bg-red-50 text-red-800',
    blue: 'border-blue-200 bg-blue-50 text-blue-900',
  }[tone];
  return <div className={cn('rounded-xl border px-4 py-3 text-sm', t)}>{children}</div>;
}

/** 카드 고르기 — 셀렉트 대신 눌러 고르는 줄. 끝 4자리만 안다 */
function CardPicker({
  cards,
  picked,
  onPick,
  onAdd,
  available,
}: {
  cards: BillingCard[];
  picked: string;
  onPick: (id: string) => void;
  onAdd: () => void;
  available: boolean;
}) {
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {cards.map((c) => {
        const on = c.id === picked;
        return (
          <button
            key={c.id}
            type="button"
            onClick={() => onPick(c.id)}
            className={cn(
              'flex items-center gap-3 rounded-xl border bg-white px-4 py-3 text-left transition',
              on ? 'border-live ring-2 ring-live/20' : 'border-slate-200 hover:border-slate-300',
            )}
            aria-pressed={on}
          >
            <span className="grid h-8 w-12 shrink-0 place-items-center rounded-md bg-gradient-to-br from-slate-700 to-slate-900 font-semibold text-[10px] text-white">
              {c.last4}
            </span>
            <span className="grid min-w-0">
              <span className="truncate font-medium text-slate-900 text-sm">{c.cardName}</span>
              <span className="text-slate-500 text-xs tabular-nums">•••• {c.last4}</span>
            </span>
            <span
              className={cn(
                'ml-auto h-4 w-4 shrink-0 rounded-full border',
                on ? 'border-[5px] border-live' : 'border-slate-300',
              )}
            />
          </button>
        );
      })}
      <button
        type="button"
        onClick={onAdd}
        disabled={!available}
        className="flex min-h-14 items-center justify-center gap-1 rounded-xl border border-slate-300 border-dashed bg-white px-4 py-3 text-slate-600 text-sm transition hover:border-live hover:text-live disabled:opacity-50"
      >
        + {cards.length === 0 ? '결제 카드 등록' : '다른 카드 등록'}
      </button>
    </div>
  );
}

/** 시작 카드의 구역 하나 — 번호 · 제목 · 내용 */
function OfferStep({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <section className="grid gap-3">
      <h3 className="flex items-center gap-2 font-semibold text-slate-900 text-sm">
        <span className="grid h-5 w-5 place-items-center rounded-full bg-slate-900 font-semibold text-[11px] text-white">
          {n}
        </span>
        {title}
      </h3>
      <div className="grid gap-2">{children}</div>
    </section>
  );
}

function OfferCard({
  sub,
  cards,
  available,
  onAddCard,
}: {
  sub: PayerSubscription;
  cards: BillingCard[];
  available: boolean;
  onAddCard: (brandId: string) => void;
}) {
  const start = useApiMutation(billing.startSubscription);
  const mine = cardsFor(cards, sub);
  const [cardId, setCardId] = useState('');
  const [consent, setConsent] = useState(false);
  const picked = cardId || mine[0]?.id || '';
  const blocked = sub.lastFailure?.blocked ?? false;
  // 0 원 구독 — 결제하지 않으니 카드가 필요 없다(261015-subscription-unify)
  const free = sub.price.amountKrw === 0;
  let step = 0;
  return (
    <Card className="overflow-hidden rounded-2xl shadow-sm">
      <div className="grid gap-6 p-6">
        <PlanHeader
          sub={sub}
          pills={<Pill tone="blue">{sub.startOn ? `${dot(sub.startOn)} 시작 예정` : '시작 전'}</Pill>}
        />
        <PlanPrice sub={sub} />
        <div className="grid gap-3 sm:grid-cols-3">
          <Fact label="이용 시작">{sub.startOn ? dot(sub.startOn) : '시작하는 날'}</Fact>
          <Fact label={free ? '이용' : '결제일'}>
            {free ? '결제 없음' : sub.startOn ? `매월 ${Number(sub.startOn.slice(8, 10))}일` : '매월 시작한 날'}
          </Fact>
          <Fact label="결제 방식">{free ? '무료' : '카드 자동결제'}</Fact>
        </div>
        <ServiceSpec />
      </div>

      <div className="grid gap-6 border-slate-100 border-t bg-slate-50/60 p-6">
        {free ? null : (
          <OfferStep n={++step} title="결제 카드">
            <CardPicker
              cards={mine}
              picked={picked}
              onPick={setCardId}
              onAdd={() => onAddCard(sub.brandId)}
              available={available}
            />
          </OfferStep>
        )}
        <OfferStep n={++step} title="확인 사항">
          <div className="grid gap-3 rounded-xl border border-slate-200 bg-white p-4 text-sm">
            <TermsSummary
              subject={{
                brandName: sub.brandName,
                query: sub.query?.query ?? null,
                amountKrw: sub.price.amountKrw,
                startOn: sub.startOn,
              }}
            />
            <a
              href="/subscription-terms"
              target="_blank"
              rel="noopener"
              className="w-fit text-live text-xs hover:underline"
            >
              구독 서비스 이용약관 전문 보기 ↗
            </a>
          </div>
        </OfferStep>

        <div className="grid gap-4">
          {sub.lastFailure ? <Callout tone={blocked ? 'amber' : 'red'}>{sub.lastFailure.message}</Callout> : null}
          <label className="flex cursor-pointer items-start gap-2.5 text-slate-700 text-sm">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 accent-[var(--color-live)]"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
            />
            <span>위 내용과 구독 서비스 이용약관(정기결제 포함)을 확인했고 동의합니다.</span>
          </label>
          <FieldError>{start.error?.message}</FieldError>
          <Button
            variant="live"
            size="lg"
            className="w-full sm:w-auto sm:justify-self-end"
            disabled={(!free && (!available || !picked)) || !consent || start.isPending || blocked}
            onClick={() =>
              start.mutate({
                params: { id: sub.id },
                body: { cardId: free ? null : picked, consent: true, termsVersion: SUBSCRIPTION_TERMS_VERSION },
              })
            }
          >
            {free
              ? start.isPending
                ? '시작하는 중…'
                : '무료로 시작'
              : !available
                ? '결제 준비 중'
                : start.isPending
                  ? '결제하는 중…'
                  : `${won(sub.price.amountKrw)} 결제하고 시작`}
          </Button>
        </div>
      </div>
    </Card>
  );
}

function LiveSubscriptionCard({
  sub,
  cards,
  available,
  onAddCard,
}: {
  sub: PayerSubscription;
  cards: BillingCard[];
  available: boolean;
  onAddCard: (brandId: string) => void;
}) {
  const change = useApiMutation(billing.changeCard);
  const retry = useApiMutation(billing.retrySubscription);
  const [changing, setChanging] = useState(false);
  const [cardId, setCardId] = useState('');
  const [consent, setConsent] = useState(false);
  const mine = cardsFor(cards, sub);
  const st = STATUS[sub.status];
  const blocked = sub.lastFailure?.blocked ?? false;
  const free = sub.price.amountKrw === 0;
  // 카드 없이 이어지는 구독 — 카드를 붙이면 다음 결제일부터 자동결제다(동의 필요, 261015-subscription-unify)
  const attaching = sub.payer === null;
  // 마지막 이용일 뒤의 기간은 결제하지 않는다 — 그 날까지만 보인다
  const nextCharge =
    sub.status !== 'canceled' && sub.nextChargeOn && !(sub.endOn && sub.endOn < sub.nextChargeOn)
      ? sub.nextChargeOn
      : null;
  const live = sub.status === 'active' || sub.status === 'past_due';
  const picked = cardId || mine[0]?.id || '';
  return (
    <Card className={cn('overflow-hidden rounded-2xl shadow-sm', sub.status === 'canceled' && 'opacity-75')}>
      <div className="grid gap-6 p-6">
        <PlanHeader
          sub={sub}
          pills={
            <>
              <Pill tone={st.tone}>{st.label}</Pill>
              {sub.endOn && sub.status !== 'canceled' ? <Pill tone="amber">{dot(sub.endOn)}까지 이용</Pill> : null}
            </>
          }
        />
        <PlanPrice sub={sub} />
        <div className={cn('grid gap-3', free ? 'sm:grid-cols-1' : 'sm:grid-cols-3')}>
          <Fact label="이번 이용 기간">
            {sub.currentPeriodStart ? `${dot(sub.currentPeriodStart)} ~ ${dot(sub.currentPeriodEnd ?? '')}` : '-'}
          </Fact>
          {free ? null : (
            <>
              <Fact label="다음 결제일">
                {nextCharge && sub.payer ? dot(nextCharge) : nextCharge ? '카드 등록 후 자동결제' : '-'}
              </Fact>
              <Fact label="결제 카드">
                {sub.payer?.card ? (
                  <span>
                    {sub.payer.card.cardName} <span className="text-slate-500">•••• {sub.payer.card.last4}</span>
                  </span>
                ) : sub.payer ? (
                  `${sub.payer.name ?? '다른 소속자'}님의 카드`
                ) : (
                  <span className="text-slate-500">등록 전</span>
                )}
              </Fact>
            </>
          )}
        </div>
        {sub.needsCard ? (
          <Callout tone="amber">
            지금 카드로는 다음 결제를 할 수 없습니다(카드 주인이 이 브랜드에서 빠졌거나 카드가 지워졌습니다). 내 카드로
            바꿔 주세요.
          </Callout>
        ) : null}
        {sub.status === 'past_due' && sub.lastFailure ? <Callout tone="red">{sub.lastFailure.message}</Callout> : null}
        {attaching && !free && sub.status === 'active' ? (
          <Callout tone="blue">
            결제 카드를 등록하면 {nextCharge ? `${dot(nextCharge)}부터` : '다음 결제일부터'} 매월 자동결제됩니다.
          </Callout>
        ) : null}
      </div>

      {live && !free ? (
        <div className="grid gap-4 border-slate-100 border-t bg-slate-50/60 px-6 py-5">
          {changing ? (
            <>
              <CardPicker
                cards={mine}
                picked={picked}
                onPick={setCardId}
                onAdd={() => onAddCard(sub.brandId)}
                available={available}
              />
              {attaching ? (
                <label className="flex cursor-pointer items-start gap-2.5 text-slate-700 text-sm">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 accent-[var(--color-live)]"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                  />
                  <span>
                    구독 서비스{' '}
                    <a href="/subscription-terms" target="_blank" rel="noopener" className="text-live hover:underline">
                      이용약관
                    </a>
                    (정기결제 포함)에 동의합니다
                  </span>
                </label>
              ) : null}
              <div className="flex flex-wrap justify-end gap-2">
                <Button variant="ghost" onClick={() => setChanging(false)}>
                  취소
                </Button>
                <Button
                  variant="live"
                  disabled={mine.length === 0 || change.isPending || blocked || (attaching && !consent)}
                  onClick={() =>
                    change.mutate(
                      {
                        params: { id: sub.id },
                        body: attaching
                          ? { cardId: picked, consent: true, termsVersion: SUBSCRIPTION_TERMS_VERSION }
                          : { cardId: picked },
                      },
                      { onSuccess: () => setChanging(false) },
                    )
                  }
                >
                  {attaching ? '자동결제 시작' : sub.status === 'past_due' ? '바꾸고 결제' : '이 카드로 바꾸기'}
                </Button>
              </div>
            </>
          ) : (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span className="text-slate-500 text-xs">해지·환불은 담당자에게 요청해 주세요.</span>
              <div className="flex flex-wrap gap-2">
                {sub.status === 'past_due' && sub.payer?.mine ? (
                  <Button
                    variant="live"
                    disabled={retry.isPending || blocked}
                    onClick={() => retry.mutate({ params: { id: sub.id } })}
                  >
                    다시 결제
                  </Button>
                ) : null}
                <Button
                  variant={attaching ? 'live' : 'outline'}
                  className={attaching ? undefined : 'bg-white'}
                  onClick={() => (mine.length === 0 ? onAddCard(sub.brandId) : setChanging(true))}
                  disabled={mine.length === 0 && !available}
                >
                  {mine.length === 0 ? '결제 카드 등록' : attaching ? '결제 카드 연결' : '결제 카드 바꾸기'}
                </Button>
              </div>
            </div>
          )}
          <FieldError>{(change.error ?? retry.error)?.message}</FieldError>
        </div>
      ) : null}
    </Card>
  );
}

function CardsCard({ cards, onAdd, available }: { cards: BillingCard[]; onAdd: () => void; available: boolean }) {
  const del = useApiMutation(billing.deleteCard);
  return (
    <Card className="rounded-2xl shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2 px-6 pt-5">
        <div>
          <h2 className="font-semibold text-slate-900">결제 카드</h2>
          <p className="text-slate-500 text-xs">내 계정의 카드 — 내가 관리하는 브랜드 어느 구독에도 쓸 수 있습니다</p>
        </div>
        <Button size="sm" variant="outline" onClick={onAdd} disabled={!available}>
          + 카드 등록
        </Button>
      </div>
      <div className="grid gap-3 p-6 sm:grid-cols-2 lg:grid-cols-3">
        {cards.length === 0 ? (
          <div className="grid place-items-center rounded-xl border border-slate-300 border-dashed px-4 py-8 text-slate-500 text-sm sm:col-span-2 lg:col-span-3">
            등록한 카드가 없습니다
          </div>
        ) : null}
        {cards.map((c) => (
          <div
            key={c.id}
            className="relative grid aspect-[1.75] content-between overflow-hidden rounded-xl bg-gradient-to-br from-slate-800 via-slate-900 to-[#06101f] p-4 text-white shadow-md"
          >
            <div className="pointer-events-none absolute -top-10 -right-10 h-32 w-32 rounded-full bg-white/5" />
            <div className="flex items-start justify-between gap-2">
              <span className="font-semibold text-sm">{c.cardName}</span>
              <button
                type="button"
                className="rounded-md px-1.5 py-0.5 text-white/60 text-xs transition hover:bg-white/10 hover:text-white disabled:opacity-50"
                disabled={del.isPending}
                onClick={() => {
                  if (confirm(`${cardLabel(c)} 카드를 지웁니다.`)) del.mutate({ params: { id: c.id } });
                }}
              >
                삭제
              </button>
            </div>
            <div className="font-medium text-lg tabular-nums tracking-[0.2em]">•••• •••• •••• {c.last4}</div>
            <div className="flex items-end justify-between text-white/70 text-xs">
              <span className="truncate">{c.holderName}</span>
              <span className="tabular-nums">
                {c.expiryMmyy.slice(0, 2)}/{c.expiryMmyy.slice(2)}
              </span>
            </div>
          </div>
        ))}
      </div>
      {del.error ? (
        <div className="px-6 pb-5">
          <FieldError>{del.error.message}</FieldError>
        </div>
      ) : null}
    </Card>
  );
}

/** 결제 한 건의 상태 — 결제자에게 보이는 말 */
function chargeState(g: PayerCharge): { label: string; tone: keyof typeof PILL_TONE } {
  if (g.status === 'paid') return { label: '결제 완료', tone: 'green' };
  if (g.status === 'refunded') return { label: '환불', tone: 'slate' };
  if (g.status === 'pending' || g.checking) return { label: '확인 중', tone: 'amber' };
  return { label: '결제 실패', tone: 'red' };
}

/** 2026-10-20T01:23:45Z → 2026.10.20 (브라우저 시간대 — 결제 시각 표시용) */
const dayOf = (isoAt: string) => {
  const d = new Date(isoAt);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * 결제 내역 — 결제 범위 브랜드 전부의 결제 기록, 최신순 10건씩.
 * ★ 남이 낸 결제는 카드 정보 없이 누가 냈는지만 보인다(서버가 그렇게 준다 — `PayerCharge.payer`)
 */
function ChargesCard() {
  const [page, setPage] = useState(1);
  const data = useApi(billing.listMyCharges, { query: { page } });
  const d = data.data;
  const pages = d ? Math.max(1, Math.ceil(d.total / d.pageSize)) : 1;
  return (
    <Card className="rounded-2xl shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2 px-6 pt-5">
        <div>
          <h2 className="font-semibold text-slate-900">결제 내역</h2>
          <p className="text-slate-500 text-xs">
            카드로 결제된 이용료 · 최신순 · 결제 증빙은 카드 매출전표로 갈음합니다
          </p>
        </div>
        {d && d.total > 0 ? <span className="text-slate-500 text-xs tabular-nums">총 {d.total}건</span> : null}
      </div>
      <div className="p-6 pt-4">
        {data.isLoading ? <Loading /> : null}
        {data.error ? <ErrorBox error={data.error} /> : null}
        {d && d.total === 0 ? (
          <div className="grid place-items-center rounded-xl border border-slate-300 border-dashed px-4 py-8 text-slate-500 text-sm">
            결제 내역이 없습니다
          </div>
        ) : null}
        {d && d.charges.length > 0 ? (
          <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200">
            {d.charges.map((g) => {
              const st = chargeState(g);
              return (
                <li
                  key={g.id}
                  className="grid gap-2 px-4 py-3 sm:grid-cols-[96px_minmax(0,1fr)_auto] sm:items-center sm:gap-4"
                >
                  <div className="text-slate-500 text-xs tabular-nums sm:text-sm">{dayOf(g.at)}</div>
                  <div className="grid min-w-0 gap-0.5">
                    <div className="truncate font-medium text-slate-900 text-sm">
                      {g.brandName} <span className="font-normal text-slate-500">· {g.subscriptionName}</span>
                    </div>
                    <div className="flex flex-wrap gap-x-3 text-slate-500 text-xs tabular-nums">
                      <span>
                        이용 기간 {dot(g.periodStart)} ~ {dot(g.periodEnd)}
                      </span>
                      <span>
                        {g.payer.mine
                          ? g.payer.card
                            ? `${g.payer.card.cardName} •••• ${g.payer.card.last4}`
                            : '내 카드'
                          : `${g.payer.name ?? '다른 소속자'}님 결제`}
                      </span>
                      {g.refundedAt ? <span>환불 {dayOf(g.refundedAt)}</span> : null}
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-3 sm:justify-end">
                    <Pill tone={st.tone}>{st.label}</Pill>
                    <span
                      className={cn(
                        'w-28 text-right font-semibold tabular-nums',
                        g.status === 'paid' ? 'text-slate-900' : 'text-slate-400 line-through decoration-slate-300',
                      )}
                    >
                      {won(g.price.amountKrw)}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : null}
        {d && pages > 1 ? (
          <div className="mt-4 flex items-center justify-center gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              이전
            </Button>
            <span className="px-2 text-slate-600 text-sm tabular-nums">
              {page} / {pages}
            </span>
            <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
              다음
            </Button>
          </div>
        ) : null}
      </div>
    </Card>
  );
}

/** 결제 화면 — 내 결제 범위(소속 브랜드)의 **모든 브랜드**를 보여 준다. 브랜드 이름은 카드·줄마다 붙어 있다 */
export function BillingPage() {
  const data = useApi(billing.getBilling);
  const [cardFor, setCardFor] = useState<string | null | undefined>(undefined);
  if (data.isLoading) return <Loading />;
  if (data.error) return <ErrorBox error={data.error} />;
  const d = data.data!;
  const offers = d.subscriptions.filter((s) => s.status === 'offered');
  const others = d.subscriptions.filter((s) => s.status !== 'offered');
  return (
    <>
      <PageTitle title="구독 관리" />
      <TestModeBanner on={d.testMode} />
      <div className="grid gap-5">
        {offers.length > 0 ? (
          <section className="grid gap-3">
            {offers.map((s) => (
              <OfferCard
                key={s.id}
                sub={s}
                cards={d.cards}
                available={d.available}
                onAddCard={(id) => setCardFor(id)}
              />
            ))}
          </section>
        ) : null}
        {/* 진행 중·끝난 구독이 있을 때만 — 제안만 있는 화면에 빈 카드를 두지 않는다 */}
        {others.length > 0 ? (
          <section className="grid gap-3">
            {others.map((s) => (
              <LiveSubscriptionCard
                key={s.id}
                sub={s}
                cards={d.cards}
                available={d.available}
                onAddCard={(id) => setCardFor(id)}
              />
            ))}
          </section>
        ) : null}
        {others.length === 0 && offers.length === 0 ? (
          <Card className="rounded-2xl">
            <CardBody className="py-10 text-sm">
              <Empty>제안된 구독이 없습니다. 담당자에게 문의해 주세요.</Empty>
            </CardBody>
          </Card>
        ) : null}
        <ChargesCard />
        <CardsCard cards={d.cards} onAdd={() => setCardFor(null)} available={d.available} />
      </div>
      <CardDialog open={cardFor !== undefined} onClose={() => setCardFor(undefined)} />
    </>
  );
}

/** 결제 대기 — 운영자가 질문·브랜드를 확정하고 구독을 제안했다. 첫 결제가 끝나야 보고서가 열린다 */
export function PaymentPending({ me }: { me: Me }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  // 대행사 고객은 결제하지 않는다 — 대행사가 진행한다(261025-agency-white-label). 결제 화면 대신 안내만
  if (me.agencyRole === 'client') {
    return (
      <div className="mx-auto max-w-xl px-4 py-16 text-center">
        <div className="font-semibold text-lg">서비스 준비 중입니다</div>
        <p className="mt-2 text-slate-600">
          담당 대행사가 이용 절차를 진행하고 있습니다. 준비가 끝나면 보고서가 열립니다.
        </p>
        <div className="mt-8 text-left">
          <SupportContact />
        </div>
      </div>
    );
  }
  return (
    <>
      {me.impersonator ? <ImpersonationBanner targetEmail={me.email} adminEmail={me.impersonator.email} /> : null}
      <div className="mx-auto max-w-4xl px-4 py-8">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-2">
          <div className="font-semibold text-lg">구독을 시작해 주세요</div>
          {me.impersonator ? null : (
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                await call(auth.logout);
                qc.clear();
                await navigate({ to: pathsFor(me.role).login });
              }}
            >
              로그아웃
            </Button>
          )}
        </div>
        <BillingPage />
        <div className="mt-6">
          <SupportContact />
        </div>
      </div>
    </>
  );
}

// ── 운영자 ───────────────────────────────────────────────────────────────

const ADMIN_STATUS = STATUS;

/**
 * 결제 취소 — 페이업 `/cancel2` 로 **건별 전액**을 취소한다(부분 취소는 규격에 없다, 261011-subscription-billing-payup).
 * ★ 결제 취소는 구독을 끝내지 않는다 — 구독도 끝낼지는 여기서 함께 고른다(기본은 그대로).
 */
function RefundDialog({
  g,
  title,
  open,
  onClose,
}: {
  g: AdminCharge;
  title: string;
  open: boolean;
  onClose: () => void;
}) {
  const refund = useApiMutation(billing.refundCharge);
  const cancel = useApiMutation(billing.cancelSubscription);
  const [reason, setReason] = useState('');
  const [alsoCancel, setAlsoCancel] = useState(false);
  const close = () => {
    setReason('');
    setAlsoCancel(false);
    refund.reset();
    cancel.reset();
    onClose();
  };
  const busy = refund.isPending || cancel.isPending;
  return (
    <Dialog open={open} onClose={close} title="결제 취소">
      <div className="grid gap-4 text-sm">
        <dl className="grid gap-2 rounded-lg bg-slate-50 px-4 py-3">
          <div className="flex justify-between gap-3">
            <dt className="text-slate-500">구독</dt>
            <dd className="text-right font-medium text-slate-900">{title}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-slate-500">이용 기간</dt>
            <dd className="tabular-nums">
              {g.periodStart} ~ {g.periodEnd}
            </dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-slate-500">결제</dt>
            <dd className="text-right">
              {g.payerEmail ?? '—'} {g.card ? `· ${cardLabel(g.card)}` : ''}
            </dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-slate-500">주문번호</dt>
            <dd>
              <code>{g.orderNumber}</code>
            </dd>
          </div>
          <div className="flex justify-between gap-3 border-slate-200 border-t pt-2">
            <dt className="font-medium text-slate-900">취소 금액</dt>
            <dd className="font-bold text-base text-slate-900 tabular-nums">{won(g.price.amountKrw)} (전액)</dd>
          </div>
        </dl>
        <p className="text-slate-600 text-xs">
          카드 승인이 전액 취소됩니다. 부분 취소는 되지 않습니다(페이업 자동결제 규격). 취소한 결제는 되돌릴 수
          없습니다.
        </p>
        <Label>
          취소 사유 (필수 — 결제 내역에 남는다)
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={200}
            placeholder="예) 고객 요청 — 중복 결제"
          />
        </Label>
        <fieldset className="grid gap-2">
          <legend className="mb-1 font-medium text-slate-900">구독은</legend>
          <label className="flex cursor-pointer items-start gap-2">
            <input type="radio" className="mt-1" checked={!alsoCancel} onChange={() => setAlsoCancel(false)} />
            <span>
              그대로 둔다 <span className="text-slate-500 text-xs">— 다음 결제일에 다시 결제된다</span>
            </span>
          </label>
          <label className="flex cursor-pointer items-start gap-2">
            <input type="radio" className="mt-1" checked={alsoCancel} onChange={() => setAlsoCancel(true)} />
            <span>
              지금 해지한다 <span className="text-slate-500 text-xs">— 이용이 오늘로 끝나고 더 결제하지 않는다</span>
            </span>
          </label>
        </fieldset>
        <FieldError>{(refund.error ?? cancel.error)?.message}</FieldError>
        {refund.isSuccess && cancel.error ? (
          <p className="text-amber-700 text-xs">
            결제는 취소됐습니다. 구독 해지만 실패했으니 구독 칸에서 다시 해지해 주세요.
          </p>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={close} disabled={busy}>
            닫기
          </Button>
          <Button
            variant="danger"
            disabled={!reason.trim() || busy || refund.isSuccess}
            onClick={() =>
              refund.mutate(
                { params: { id: g.id }, body: { reason: reason.trim() } },
                {
                  onSuccess: () => {
                    if (!alsoCancel) return close();
                    cancel.mutate(
                      { params: { id: g.subscriptionId }, body: { when: 'now', reason: 'requested' } },
                      { onSuccess: close },
                    );
                  },
                },
              )
            }
          >
            {busy ? '취소하는 중…' : `${won(g.price.amountKrw)} 결제 취소`}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function ChargeLine({ g }: { g: AdminCharge }) {
  const [refunding, setRefunding] = useState(false);
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-slate-100 border-b py-1.5 text-xs last:border-b-0">
      <span className="w-36 text-slate-500">{dateTime(g.createdAt)}</span>
      <span>{g.kind === 'initial' ? '첫 결제' : '갱신'}</span>
      <span>
        {g.periodStart}~{g.periodEnd}
      </span>
      <span>{won(g.price.amountKrw)}</span>
      <Badge
        tone={
          g.status === 'paid' ? 'green' : g.status === 'failed' ? 'red' : g.status === 'refunded' ? 'slate' : 'amber'
        }
      >
        {g.status}
        {g.failureReason ? `·${g.failureReason}` : ''}
      </Badge>
      <span className="text-slate-500">
        {g.payerEmail ?? ''} {g.card ? cardLabel(g.card) : ''}
      </span>
      <code className="text-slate-500">{g.orderNumber}</code>
      {g.pgTransactionId ? <code>tx {g.pgTransactionId}</code> : null}
      {g.pgMessage ? <span className="text-slate-500">{g.pgMessage}</span> : null}
      {g.refundedAt ? (
        <span className="text-slate-500">
          환불 {dateTime(g.refundedAt)} · {g.refundReason}
        </span>
      ) : null}
      {g.status === 'paid' && g.pgTransactionId ? (
        <>
          <Button size="sm" variant="ghost" className="ml-auto text-bad" onClick={() => setRefunding(true)}>
            결제 취소
          </Button>
          <RefundDialog
            g={g}
            title={`${g.periodStart} ~ ${g.periodEnd} 결제`}
            open={refunding}
            onClose={() => setRefunding(false)}
          />
        </>
      ) : null}
    </div>
  );
}

/** 운영자 화면의 상태 이름 — 제안은 「아직 시작하지 않은 구독」임을 말로 적는다 */
const ADMIN_LABEL: Record<AdminSubscription['status'], string> = {
  offered: '제안 · 시작 전',
  active: '이용 중',
  past_due: '결제 밀림',
  canceled: '종료',
};

/** 사실 한 줄 — 이름과 값 */
function AdminFact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid gap-0.5">
      <dt className="text-slate-500 text-xs">{label}</dt>
      <dd className="text-slate-900 text-sm tabular-nums">{children}</dd>
    </div>
  );
}

const FAILURE_LABEL = { declined: '카드 거절', no_response: '결과 모름', ineligible: '카드 자격 없음' } as const;

/** 운영자 결제 내역 한 줄 — 언제 · 누구 · 무엇 · 얼마 · 상태 · PG 열쇠(주문번호) · 환불 */
function AdminChargeRow({ g, showBrand }: { g: AdminChargeListItem; showBrand: boolean }) {
  const [refunding, setRefunding] = useState(false);
  const st =
    g.status === 'paid'
      ? { label: '결제 완료', tone: 'green' as const }
      : g.status === 'refunded'
        ? { label: '환불', tone: 'slate' as const }
        : g.status === 'pending'
          ? { label: '처리 중', tone: 'amber' as const }
          : g.failureReason === 'no_response' && !g.reviewedAt
            ? { label: '결과 확인 필요', tone: 'red' as const }
            : {
                label: `실패 · ${g.failureReason ? FAILURE_LABEL[g.failureReason] : '사유 없음'}`,
                tone: 'red' as const,
              };
  return (
    <li className="grid gap-2 px-4 py-3 text-sm lg:grid-cols-[132px_minmax(0,1.4fr)_minmax(0,1fr)_21rem] lg:items-center lg:gap-4">
      <div className="text-slate-500 text-xs tabular-nums">{dateTime(g.paidAt ?? g.createdAt)}</div>
      <div className="grid min-w-0 gap-0.5">
        <div className="truncate font-medium text-slate-900">
          {showBrand ? `${g.brandName} · ` : ''}
          <span className={showBrand ? 'font-normal text-slate-600' : ''}>{g.subscriptionName}</span>
        </div>
        <div className="text-slate-500 text-xs tabular-nums">
          {g.kind === 'initial' ? '첫 결제' : '갱신'} · 이용 기간 {g.periodStart} ~ {g.periodEnd}
        </div>
      </div>
      <div className="grid min-w-0 gap-0.5 text-xs">
        <span className="truncate text-slate-700">
          {g.payerEmail ?? '—'} {g.card ? `· ${cardLabel(g.card)}` : ''}
        </span>
        <span className="truncate text-slate-500">
          주문번호 <code>{g.orderNumber}</code>
          {g.pgTransactionId ? (
            <>
              {' '}
              · 거래 <code>{g.pgTransactionId}</code>
            </>
          ) : null}
        </span>
        {g.pgMessage && g.status !== 'paid' ? <span className="truncate text-bad">{g.pgMessage}</span> : null}
        {g.refundedAt ? (
          <span className="text-slate-500">
            환불 {dateTime(g.refundedAt)} {g.refundedBy ?? ''} · {g.refundReason}
          </span>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 lg:justify-end">
        <Pill tone={st.tone}>{st.label}</Pill>
        <span
          className={cn(
            'w-28 text-right font-semibold tabular-nums',
            g.status === 'paid' ? 'text-slate-900' : 'text-slate-400 line-through decoration-slate-300',
          )}
        >
          {won(g.price.amountKrw)}
        </span>
        {/* 버튼이 없는 줄도 같은 폭을 비워 둔다 — 금액 칸이 줄마다 어긋나지 않게 */}
        <span className="w-20 text-right">
          {g.status === 'paid' && g.pgTransactionId ? (
            <Button size="sm" variant="ghost" className="text-bad" onClick={() => setRefunding(true)}>
              결제 취소
            </Button>
          ) : null}
        </span>
      </div>
      <RefundDialog
        g={g}
        title={`${g.brandName} · ${g.subscriptionName}`}
        open={refunding}
        onClose={() => setRefunding(false)}
      />
    </li>
  );
}

/** 운영자 결제 내역 — 브랜드를 주면 그 브랜드만, 아니면 전체. 최신순 20건씩 */
function AdminChargesList({ brandId }: { brandId?: string }) {
  const [page, setPage] = useState(1);
  // 전체 목록은 「실제 | 테스트」 탭을 따른다 — 브랜드 하나면 그 브랜드다(261023-test-brand)
  const brandKind = useBrandKind();
  const data = useApi(billing.listAdminCharges, {
    query: brandId ? { brandId, page } : { page, brandKind },
  });
  const d = data.data;
  const pages = d ? Math.max(1, Math.ceil(d.total / d.pageSize)) : 1;
  return (
    <div className="grid gap-3">
      {data.isLoading ? <Loading /> : null}
      {data.error ? <ErrorBox error={data.error} /> : null}
      {d && d.total === 0 ? (
        <div className="rounded-xl border border-slate-300 border-dashed px-4 py-6 text-center text-slate-500 text-sm">
          결제 내역이 없습니다
        </div>
      ) : null}
      {d && d.charges.length > 0 ? (
        <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
          {d.charges.map((g) => (
            <AdminChargeRow key={g.id} g={g} showBrand={!brandId} />
          ))}
        </ul>
      ) : null}
      {d && pages > 1 ? (
        <div className="flex items-center justify-center gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            이전
          </Button>
          <span className="px-2 text-slate-600 text-sm tabular-nums">
            {page} / {pages} · 총 {d.total}건
          </span>
          <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
            다음
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * 구독 하나 — **이미 있는 구독을 관리하는 곳**이다(새 구독은 브랜드 화면 아래 「새 구독 추가」).
 * 머리(상태·이름·질문·금액) → 사실(개시일·이용 기간·다음 결제·결제 수단) → 할 일(상태별) → 결제 이력 순이다.
 */
function AdminSubscriptionRow({ s, showBrand = false }: { s: AdminSubscription; showBrand?: boolean }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(s.name);
  const [price, setPrice] = useState(String(s.price.supplyKrw));
  const [startOn, setStartOn] = useState(s.startOn ?? '');
  const [queryId, setQueryId] = useState(s.query?.id ?? '');
  const [endOn, setEndOn] = useState(s.endOn ?? '');
  const [freeStartOn, setFreeStartOn] = useState(s.startOn ?? '');
  const detail = useApi(billing.getAdminSubscription, { params: { id: s.id } }, { enabled: open });
  const cancel = useApiMutation(billing.cancelSubscription);
  const undo = useApiMutation(billing.undoCancelSubscription);
  const retry = useApiMutation(billing.adminRetry);
  const update = useApiMutation(billing.updateSubscription);
  const startFree = useApiMutation(billing.startWithoutPayment);
  const st = ADMIN_STATUS[s.status];
  const live = s.status === 'active' || s.status === 'past_due';
  const offered = s.status === 'offered';
  const free = s.price.supplyKrw === 0;
  const err = (cancel.error ?? undo.error ?? retry.error ?? update.error ?? startFree.error)?.message;
  return (
    <div
      className={cn(
        'grid gap-4 rounded-xl border bg-white p-4 text-sm',
        offered ? 'border-blue-200' : s.status === 'past_due' ? 'border-red-200' : 'border-slate-200',
        s.status === 'canceled' && 'bg-slate-50 opacity-80',
      )}
    >
      {/* 머리 — 무엇이고 얼마인가 */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="grid min-w-0 gap-1.5">
          <div className="flex flex-wrap items-center gap-1.5">
            <Pill tone={st.tone}>{ADMIN_LABEL[s.status]}</Pill>
            {s.brandStatus === 'confirmed' ? <Pill tone="amber">결제 대기 고객</Pill> : null}
            {s.needsCard ? <Pill tone="amber">카드 자격 없음</Pill> : null}
            {s.lastFailure?.blocked ? <Pill tone="red">결과 확인 필요</Pill> : null}
            {s.endOn && s.status !== 'canceled' ? <Pill tone="amber">{s.endOn}까지 이용</Pill> : null}
          </div>
          <div className="font-semibold text-slate-900">
            {showBrand ? <span className="text-slate-500">{s.brandName} · </span> : null}
            {s.name}
          </div>
          {s.query ? (
            <div className={cn('text-xs', s.query.deleted ? 'text-bad' : 'text-slate-600')}>
              질문 「{s.query.query}」{s.query.deleted ? ' — 삭제된 질문이다. 수정에서 다른 질문으로 바꾼다' : ''}
            </div>
          ) : (
            <div className="text-bad text-xs">질문 없음 — 수정에서 질문을 고른다</div>
          )}
        </div>
        <div className="grid justify-items-end gap-0.5 tabular-nums">
          <div className="font-bold text-lg text-slate-900">
            {free ? '무료' : won(s.price.amountKrw)}
            {free ? null : <span className="font-normal text-slate-500 text-xs"> / 월</span>}
          </div>
          <div className="text-slate-500 text-xs">{free ? '0 원' : `공급가 ${won(s.price.supplyKrw)} + 부가세`}</div>
        </div>
      </div>

      {/* 사실 */}
      <dl className="grid gap-x-6 gap-y-3 rounded-lg bg-slate-50 px-4 py-3 sm:grid-cols-2 lg:grid-cols-4">
        <AdminFact label="구독 개시일">{s.startOn ?? (offered ? '고객이 결제한 날' : '—')}</AdminFact>
        <AdminFact label="이번 이용 기간">
          {s.currentPeriodStart ? `${s.currentPeriodStart} ~ ${s.currentPeriodEnd}` : '—'}
        </AdminFact>
        <AdminFact label="다음 결제일">{s.nextChargeOn ?? '—'}</AdminFact>
        <AdminFact label="결제 수단">
          {s.payer ? (
            `${s.payer.name ?? s.payer.email} · ${cardLabel(s.payer.card)}`
          ) : live ? (
            <span className="text-slate-600">카드 없음 — 결제 없이 이어짐</span>
          ) : (
            '—'
          )}
        </AdminFact>
      </dl>
      {s.startedAt || s.termsVersion || s.canceledAt || s.priceChangedAt ? (
        <div className="-mt-2 flex flex-wrap gap-x-4 gap-y-1 text-slate-500 text-xs">
          {s.startedAt ? (
            <span>
              시작 {dateTime(s.startedAt)} {s.startedBy ?? ''}
            </span>
          ) : null}
          {s.termsVersion ? <span>약관 {s.termsVersion} 판 동의</span> : null}
          {s.priceChangedAt ? <span>가격 변경 {dateTime(s.priceChangedAt)}</span> : null}
          {s.canceledAt ? (
            <span>
              해지 {dateTime(s.canceledAt)} {s.canceledBy ?? ''} ({s.cancelReason})
            </span>
          ) : null}
        </div>
      ) : null}
      {s.lastFailure && s.status !== 'canceled' ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-800 text-xs">
          마지막 결제 실패: {s.lastFailure.reason} · {s.lastFailure.message}
        </div>
      ) : null}

      {/* 수정 — 열면 이름표가 붙은 칸으로 */}
      {editing ? (
        <div className="grid gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
          <div className="font-medium text-slate-900">구독 수정</div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Label>
              구독 이름
              <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={100} />
            </Label>
            <Label>
              월 공급가 (원 · 부가세 별도)
              <Input inputMode="numeric" value={price} onChange={(e) => setPrice(e.target.value.replace(/\D/g, ''))} />
            </Label>
            <Label>
              질문
              <QueryPicker brandId={s.brandId} value={queryId} onChange={setQueryId} current={s.query} />
            </Label>
            {offered ? (
              <Label>
                구독 개시일 (비우면 고객이 결제한 날)
                <Input type="date" value={startOn} onChange={(e) => setStartOn(e.target.value)} />
              </Label>
            ) : null}
            {/* 마지막 이용일 — 그날까지 이용하고, 그 뒤 기간은 결제하지 않는다(261015-subscription-unify) */}
            <Label>
              마지막 이용일 (비우면 계속 이어진다)
              <Input type="date" value={endOn} onChange={(e) => setEndOn(e.target.value)} />
            </Label>
          </div>
          {live ? (
            <p className="text-amber-700 text-xs">
              가격은 다음 결제일부터 적용된다. 고객에게 불리한 변경은 30일 전에 운영자가 직접 알린다.
            </p>
          ) : null}
          <div className="flex gap-2">
            <Button
              size="sm"
              disabled={!queryId || price === '' || update.isPending}
              onClick={() =>
                update.mutate(
                  {
                    params: { id: s.id },
                    body: {
                      name: name.trim(),
                      supplyPriceKrw: Number(price),
                      startOn: startOn || null,
                      queryId,
                      endOn: endOn || null,
                    },
                  },
                  { onSuccess: () => setEditing(false) },
                )
              }
            >
              저장
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
              취소
            </Button>
          </div>
        </div>
      ) : null}

      {/* 할 일 — 상태마다 다르다 */}
      {s.status !== 'canceled' && !editing ? (
        <div className="flex flex-wrap items-center gap-2 border-slate-100 border-t pt-3">
          {offered ? (
            // 결제 없이 시작 — 오프라인 청구·무료. 카드 없이 이어지고, 고객이 카드를 붙이면 그때부터 자동결제
            <div className="flex flex-wrap items-center gap-2 rounded-lg bg-blue-50 px-3 py-2">
              <span className="text-blue-900 text-xs">고객 결제 없이 지금 시작</span>
              <Input
                type="date"
                className="h-8 w-40 bg-white"
                value={freeStartOn}
                onChange={(e) => setFreeStartOn(e.target.value)}
                title="개시일 — 비우면 오늘"
              />
              <Button
                size="sm"
                disabled={startFree.isPending}
                onClick={() => {
                  if (confirm('결제 없이 시작합니다(오프라인 청구·무료). 결제 대기 고객이면 바로 이용이 열립니다.'))
                    startFree.mutate({ params: { id: s.id }, body: { startOn: freeStartOn || null } });
                }}
              >
                결제 없이 시작
              </Button>
            </div>
          ) : null}
          {s.status === 'past_due' ? (
            <Button
              size="sm"
              disabled={retry.isPending || s.lastFailure?.blocked}
              onClick={() => {
                if (confirm(`${won(s.price.amountKrw)}을 지금 카드로 결제합니다.`))
                  retry.mutate({ params: { id: s.id } });
              }}
            >
              재결제
            </Button>
          ) : null}
          <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
            수정
          </Button>
          <span className="ml-auto flex flex-wrap gap-2">
            {offered ? (
              <Button
                size="sm"
                variant="ghost"
                className="text-slate-600"
                onClick={() => {
                  if (confirm('제안을 철회합니다. 고객 화면에서 사라집니다.'))
                    cancel.mutate({ params: { id: s.id }, body: { when: 'now', reason: 'admin' } });
                }}
              >
                제안 철회
              </Button>
            ) : null}
            {live && !s.endOn ? (
              <>
                {s.status === 'active' ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-slate-600"
                    onClick={() => {
                      if (confirm(`${s.currentPeriodEnd} 이용 기간이 끝나면 해지합니다(고객 요청).`))
                        cancel.mutate({ params: { id: s.id }, body: { when: 'period_end', reason: 'requested' } });
                    }}
                  >
                    기간 끝에 해지
                  </Button>
                ) : null}
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-bad hover:bg-red-50"
                  onClick={() => {
                    if (confirm('지금 해지합니다. 이미 결제된 금액은 결제 이력에서 따로 환불해야 합니다.'))
                      cancel.mutate({ params: { id: s.id }, body: { when: 'now', reason: 'requested' } });
                  }}
                >
                  즉시 해지
                </Button>
              </>
            ) : null}
            {s.endOn && s.status === 'active' ? (
              <Button size="sm" variant="outline" onClick={() => undo.mutate({ params: { id: s.id } })}>
                해지 취소 (마지막 이용일 지우기)
              </Button>
            ) : null}
          </span>
        </div>
      ) : null}
      <FieldError>{err}</FieldError>
      {(offered || s.status === 'past_due') && !editing ? <PayLinkAdmin s={s} /> : null}

      {/* 결제 이력 */}
      {offered ? null : (
        <div className="grid gap-2">
          <button
            type="button"
            className="w-fit text-slate-500 text-xs hover:text-slate-900"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? '▾ 결제 이력 접기' : '▸ 결제 이력 보기'}
          </button>
          {open ? (
            <div className="rounded-lg bg-slate-50 p-2">
              {detail.isLoading ? <Loading /> : null}
              {detail.data?.charges.length === 0 ? <Empty>결제 이력이 없습니다</Empty> : null}
              {detail.data?.charges.map((g) => (
                <ChargeLine key={g.id} g={g} />
              ))}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

/**
 * 결제 링크 (261020-pay-link) — 로그인 없이 카드 등록·결제를 하는 일회성 주소. 카드 주인이 될 소속 계정을 골라 만들고
 * 열린 링크의 **주소를 계속 보여 준다**(서버가 암호문을 풀어 준다 — 「발전」). 다시 만들면 옛 주소는 죽는다
 */
function PayLinkAdmin({ s }: { s: AdminSubscription }) {
  const [picking, setPicking] = useState(false);
  const [userId, setUserId] = useState('');
  const [copied, setCopied] = useState(false);
  const members = useApi(registry.listBrandMembersAdmin, { params: { id: s.brandId } }, { enabled: picking });
  const issue = useApiMutation(billing.issuePayLink);
  const revoke = useApiMutation(billing.revokePayLink);
  const list = members.data?.members ?? [];
  const link = s.payLink;
  const what = s.status === 'offered' ? '카드 등록 + 첫 결제' : '새 카드로 밀린 결제';
  return (
    <div className="grid gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-slate-900">결제 링크</span>
        <span className="text-slate-500">로그인 없이 휴대폰에서 {what}</span>
        <span className="ml-auto flex gap-2">
          {link ? (
            <Button
              size="sm"
              variant="ghost"
              className="text-slate-600"
              disabled={revoke.isPending}
              onClick={() => {
                if (confirm('결제 링크를 회수합니다. 보낸 주소가 더는 열리지 않습니다.'))
                  revoke.mutate({ params: { id: link.id } });
              }}
            >
              회수
            </Button>
          ) : null}
          {picking ? null : (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                // 보낸 주소가 죽는다 — 열린 링크가 있으면 한 번 묻는다
                if (
                  !link ||
                  confirm('새로 만들면 지금 링크는 더는 열리지 않습니다. 이미 고객에게 보냈다면 새로 만들지 마세요.')
                )
                  setPicking(true);
              }}
            >
              {link ? '새로 만들기' : '링크 만들기'}
            </Button>
          )}
        </span>
      </div>
      {picking ? (
        <div className="flex flex-wrap items-center gap-2">
          {members.isLoading ? <Loading /> : null}
          {members.data && list.length === 0 ? (
            <span className="text-bad">이 브랜드에 소속 계정이 없습니다 — 계정을 먼저 소속시킨다</span>
          ) : null}
          {list.length > 0 ? (
            <>
              <span className="text-slate-600">카드 주인</span>
              <Select className="h-8 w-auto bg-white" value={userId} onChange={(e) => setUserId(e.target.value)}>
                <option value="">계정 고르기</option>
                {list.map((m) => (
                  <option key={m.userId} value={m.userId}>
                    {m.name ?? m.companyName ?? m.email} · {m.email} ({m.role === 'owner' ? '소유자' : '관리자'})
                  </option>
                ))}
              </Select>
              <Button
                size="sm"
                disabled={!userId || issue.isPending}
                onClick={() =>
                  issue.mutate(
                    { params: { id: s.id }, body: { userId } },
                    {
                      onSuccess: () => {
                        setCopied(false);
                        setPicking(false);
                      },
                    },
                  )
                }
              >
                만들기
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setPicking(false)}>
                취소
              </Button>
            </>
          ) : null}
        </div>
      ) : null}
      {link ? (
        <div className="grid gap-1.5 rounded-md bg-white p-2 ring-1 ring-blue-200">
          {link.url ? (
            <div className="flex gap-2">
              <Input readOnly className="h-8 font-mono text-xs" value={link.url} onFocus={(e) => e.target.select()} />
              <Button
                size="sm"
                onClick={() =>
                  void navigator.clipboard.writeText(link.url ?? '').then(
                    () => setCopied(true),
                    () => setCopied(false),
                  )
                }
              >
                {copied ? '복사됨' : '복사'}
              </Button>
            </div>
          ) : (
            <span className="text-amber-700">
              이 링크는 주소를 다시 보여 줄 수 없다(주소 저장 전에 만든 링크거나 결제 키가 없는 환경). 보낸 주소는
              그대로 열린다 — 잃어버렸으면 새로 만든다.
            </span>
          )}
          <span className="text-slate-500">
            받는 계정 {link.userName} · {dateTime(link.expiresAt)}까지 · 카드 입력 {link.attempts}회 · 한 번 결제하면
            닫힌다. 주소를 아는 사람은 누구나 이 계정 이름으로 카드를 붙여 결제할 수 있다 — 고객에게만 보낸다.
          </span>
        </div>
      ) : null}
      <FieldError>{(issue.error ?? revoke.error)?.message}</FieldError>
    </div>
  );
}

function ReviewLine({ g }: { g: AdminCharge }) {
  const review = useApiMutation(billing.reviewCharge);
  const [tx, setTx] = useState('');
  return (
    <div className="grid gap-2 border-slate-100 border-b py-3 text-sm last:border-b-0">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{g.payerEmail ?? ''}</span>
        <span>{won(g.price.amountKrw)}</span>
        <span className="text-slate-500">{dateTime(g.createdAt)}</span>
        <span>
          주문번호 <code className="rounded bg-slate-100 px-1">{g.orderNumber}</code>
        </span>
        <span className="text-slate-500">{g.pgMessage}</span>
      </div>
      <p className="text-slate-600 text-xs">
        페이업 관리자(cp.payup.co.kr)에서 이 주문번호의 승인 여부를 확인한 뒤 고른다. 같은 가맹점을 selfad 와 함께
        쓰므로 주문번호로 찾는다.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          className="w-56"
          placeholder="승인됐으면 거래번호"
          value={tx}
          onChange={(e) => setTx(e.target.value.trim())}
        />
        <Button
          size="sm"
          disabled={tx.length < 4 || review.isPending}
          onClick={() => review.mutate({ params: { id: g.id }, body: { result: 'paid', transactionId: tx } })}
        >
          승인됨 — 결제로 확정
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={review.isPending}
          onClick={() => {
            if (confirm('페이업에 승인 기록이 없음을 확인했습니까? 확인하면 다시 결제할 수 있게 됩니다.'))
              review.mutate({ params: { id: g.id }, body: { result: 'not_paid' } });
          }}
        >
          승인 안 됨
        </Button>
      </div>
      <FieldError>{review.error?.message}</FieldError>
    </div>
  );
}

export function AdminBillingPage() {
  // 「실제 | 테스트」 탭 — 구독·결과 확인·결제 내역이 전부 탭을 따른다(261023-test-brand)
  const brandKind = useBrandKind();
  const data = useApi(billing.listAdminSubscriptions, { query: { brandKind } });
  const [filter, setFilter] = useState<'attention' | 'all'>('attention');
  if (data.isLoading || data.error)
    return (
      <>
        <PageTitle title="구독·결제" />
        <BrandKindTabs />
        {data.error ? <ErrorBox error={data.error} /> : <Loading />}
      </>
    );
  const d = data.data!;
  const attention = d.subscriptions.filter(
    (s) =>
      s.status === 'past_due' ||
      s.needsCard ||
      s.lastFailure?.blocked ||
      s.status === 'offered' ||
      (s.endOn !== null && s.status !== 'canceled') ||
      (s.status !== 'canceled' && s.query === null),
  );
  const shown = filter === 'attention' ? attention : d.subscriptions;
  return (
    <>
      <PageTitle title="구독·결제" />
      <BrandKindTabs />
      <TestModeBanner on={d.testMode} />
      {!d.available ? (
        <div className="mb-4 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-red-900 text-sm">
          결제가 꺼져 있다 — 페이업 키·카드 암호화 키가 없다(RUNBOOK-admin 「결제」).
        </div>
      ) : null}
      <div className="grid gap-4">
        <Card>
          <CardHeader
            title={`결과 확인 필요 ${d.unreviewed.length}건`}
            description="응답을 받지 못한 결제 — 검토 전까지 그 구독의 결제가 막혀 있다"
          />
          <CardBody>
            {d.unreviewed.length === 0 ? (
              <Empty>없습니다</Empty>
            ) : (
              d.unreviewed.map((g) => <ReviewLine key={g.id} g={g} />)
            )}
          </CardBody>
        </Card>
        <Card>
          <CardHeader
            title="구독"
            actions={
              <Select
                value={filter}
                onChange={(e) => setFilter(e.target.value as 'attention' | 'all')}
                className="w-40"
              >
                <option value="attention">손이 필요한 것 ({attention.length})</option>
                <option value="all">전체 ({d.subscriptions.length})</option>
              </Select>
            }
          />
          <CardBody>
            {shown.length === 0 ? (
              <Empty>없습니다</Empty>
            ) : (
              shown.map((s) => <AdminSubscriptionRow key={s.id} s={s} showBrand />)
            )}
          </CardBody>
        </Card>
        <Card>
          <CardHeader
            title="결제 내역"
            description="전체 브랜드의 카드 결제 기록 — 최신순 20건씩. 결제 취소(전액)는 그 줄에서 한다"
          />
          <CardBody>
            <AdminChargesList key={brandKind} />
          </CardBody>
        </Card>
      </div>
    </>
  );
}

/**
 * 운영자 고객 상세 — 그 고객의 구독과 새 제안. 구독은 결제 수단과 무관하다(261015-subscription-unify):
 * 제안을 고객이 카드로 시작하거나, 운영자가 그 줄의 「결제 없이 시작」으로 카드 없이 시작한다(오프라인 청구·무료).
 * 기사 회차·구독일도 여기서 넣고 고친다(기사 보드는 읽기만 한다)
 */
/**
 * 브랜드 이용 상태 — 이용 중 ↔ 결제 대기 (261017-brand-status-by-operator).
 * ★ 결제가 안 돼도 자동으로 바뀌지 않는다 — Discord 「이용 중인데 결제된 구독이 없다」를 보고 운영자가 여기서 정한다
 */
function BrandStatusAdmin({ brandId, brandStatus }: { brandId: string; brandStatus: string }) {
  const set = useApiMutation(registry.setBrandStatus);
  if (brandStatus === 'pending') return null;
  const active = brandStatus === 'active';
  const next = active ? 'confirmed' : 'active';
  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-3 rounded-xl border px-4 py-3',
        active ? 'border-emerald-200 bg-emerald-50/60' : 'border-amber-200 bg-amber-50/60',
      )}
    >
      <div className="grid gap-0.5">
        <div className="flex items-center gap-2">
          <span className="font-medium text-slate-900 text-sm">브랜드 이용 상태</span>
          {active ? <Pill tone="green">이용 중</Pill> : <Pill tone="amber">결제 대기</Pill>}
        </div>
        <p className="text-slate-600 text-xs">
          {active
            ? '보고서·기사가 열려 있다. 결제가 안 돼도 자동으로 바뀌지 않는다 — 알림을 보고 여기서 바꾼다'
            : '보고서·기사가 멈추고 결제 화면만 열린다. 수집은 계속한다'}
        </p>
      </div>
      <Button
        variant="outline"
        size="sm"
        className="bg-white"
        disabled={set.isPending}
        onClick={() => {
          const msg =
            next === 'confirmed'
              ? '결제 대기로 바꿉니다. 고객의 보고서·기사가 멈추고 결제 화면만 열립니다(수집은 계속합니다). 계속할까요?'
              : '이용 중으로 바꿉니다. 결제 없이 보고서·기사가 열립니다. 계속할까요?';
          if (window.confirm(msg)) set.mutate({ params: { id: brandId }, body: { status: next } });
        }}
      >
        {next === 'confirmed' ? '결제 대기로 바꾸기' : '이용 중으로 바꾸기'}
      </Button>
      {set.error ? <ErrorBox error={set.error} /> : null}
    </div>
  );
}

/** 구역 제목 — 구역마다 무엇을 하는 곳인지 한 줄로 */
function AdminSection({
  title,
  hint,
  children,
}: {
  title: React.ReactNode;
  hint?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="grid gap-3">
      <div>
        <h3 className="font-semibold text-slate-900 text-sm">{title}</h3>
        {hint ? <p className="text-slate-500 text-xs">{hint}</p> : null}
      </div>
      {children}
    </section>
  );
}

export function BrandBillingAdmin({ brandId, brandStatus }: { brandId: string; brandStatus: string }) {
  const data = useApi(billing.listAdminSubscriptions, { query: { brandId } });
  const offer = useApiMutation(billing.offerSubscription);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState(DEFAULT_SUBSCRIPTION_NAME);
  const [price, setPrice] = useState('');
  const [startOn, setStartOn] = useState('');
  const [queryId, setQueryId] = useState('');
  const subs = data.data?.subscriptions ?? [];
  const current = subs.filter((s) => s.status !== 'canceled');
  const past = subs.filter((s) => s.status === 'canceled');
  const reset = () => {
    setName(DEFAULT_SUBSCRIPTION_NAME);
    setPrice('');
    setStartOn('');
    setQueryId('');
    offer.reset();
    setAdding(false);
  };
  return (
    <Card>
      <CardHeader title="구독·결제" description="이 브랜드의 이용 상태와 구독. 해지·환불은 운영자만 여기서 한다" />
      <CardBody className="grid gap-6">
        <BrandStatusAdmin brandId={brandId} brandStatus={brandStatus} />

        <AdminSection
          title={`현재 구독 ${current.length}건`}
          hint="이미 있는 구독을 관리한다 — 수정 · 결제 없이 시작 · 해지 · 결제 이력"
        >
          {data.isLoading ? <Loading /> : null}
          {!data.isLoading && current.length === 0 ? (
            <div className="rounded-xl border border-slate-300 border-dashed px-4 py-6 text-center text-slate-500 text-sm">
              구독이 없습니다 — 아래 「새 구독 추가」에서 만든다
            </div>
          ) : null}
          {current.map((s) => (
            <AdminSubscriptionRow key={s.id} s={s} />
          ))}
        </AdminSection>

        {past.length > 0 ? (
          <details className="group">
            <summary className="cursor-pointer list-none font-semibold text-slate-900 text-sm">
              <span className="text-slate-400 group-open:hidden">▸ </span>
              <span className="hidden text-slate-400 group-open:inline">▾ </span>
              지난 구독 {past.length}건 <span className="font-normal text-slate-500 text-xs">— 해지·철회된 것</span>
            </summary>
            <div className="mt-3 grid gap-3">
              {past.map((s) => (
                <AdminSubscriptionRow key={s.id} s={s} />
              ))}
            </div>
          </details>
        ) : null}

        <AdminSection
          title="결제 내역"
          hint="이 브랜드의 카드 결제 기록 — 최신순. 결제 없이 시작한 구독(오프라인 청구)은 기록이 없다"
        >
          <AdminChargesList brandId={brandId} />
        </AdminSection>

        <AdminSection
          title="새 구독 추가"
          hint="제안을 만들면 고객 「구독 관리」에 시작 전 구독으로 보인다. 고객이 카드로 시작하거나, 운영자가 위 목록에서 「결제 없이 시작」으로 연다"
        >
          {brandStatus === 'pending' ? (
            <p className="text-slate-500 text-sm">승인 대기 고객은 「가입 신청」에서 확정하며 구독을 제안한다</p>
          ) : !adding ? (
            <Button variant="outline" className="w-fit" onClick={() => setAdding(true)}>
              + 새 구독 제안 만들기
            </Button>
          ) : (
            <form
              className="grid gap-4 rounded-xl border border-slate-200 bg-slate-50 p-4"
              onSubmit={(e) => {
                e.preventDefault();
                offer.mutate(
                  {
                    body: {
                      brandId,
                      name: name.trim(),
                      supplyPriceKrw: Number(price),
                      startOn: startOn || null,
                      queryId,
                    },
                  },
                  { onSuccess: reset },
                );
              }}
            >
              <div className="grid gap-3 sm:grid-cols-2">
                <Label>
                  구독 이름
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    maxLength={100}
                    placeholder={DEFAULT_SUBSCRIPTION_NAME}
                  />
                </Label>
                <Label>
                  월 공급가 (원 · 부가세 별도 · 0 은 무료)
                  <Input
                    inputMode="numeric"
                    value={price}
                    onChange={(e) => setPrice(e.target.value.replace(/\D/g, ''))}
                    placeholder="1000000"
                  />
                </Label>
                <Label>
                  구독 개시일 (선택 — 비우면 고객이 결제한 날)
                  <Input type="date" value={startOn} onChange={(e) => setStartOn(e.target.value)} />
                </Label>
                <Label>
                  질문
                  <QueryPicker brandId={brandId} value={queryId} onChange={setQueryId} />
                </Label>
              </div>
              <FieldError>{offer.error?.message}</FieldError>
              <div className="flex gap-2">
                <Button type="submit" disabled={!name.trim() || price === '' || !queryId || offer.isPending}>
                  제안 만들기
                </Button>
                <Button type="button" variant="ghost" onClick={reset}>
                  취소
                </Button>
              </div>
            </form>
          )}
        </AdminSection>
      </CardBody>
    </Card>
  );
}

/**
 * 구독에 묶을 질문 — 그 브랜드의 살아 있는 질문 중 하나 (261013-subscription-query-link).
 * DB·계약은 「질문 없음」을 받지만 **지금은 모든 구독이 질문 하나와 묶이므로 화면이 반드시 고르게 한다**(빈 값이면 저장 버튼이 꺼진다).
 * 묶인 질문이 지워졌으면 그 질문도 목록에 남겨 둔다 — 그대로 두고 이름·가격만 고칠 수 있게
 */
function QueryPicker({
  brandId,
  value,
  onChange,
  current,
}: {
  brandId: string;
  value: string;
  onChange: (id: string) => void;
  current?: { id: string; query: string; deleted: boolean } | null;
}) {
  const queries = useApi(registry.listQueries, { query: { brandId } });
  const live = queries.data?.queries ?? [];
  if (queries.isLoading) return <Loading />;
  if (live.length === 0 && !current)
    return <span className="self-center text-bad text-xs">이 브랜드에 질문이 없습니다 — 질문을 먼저 등록한다</span>;
  return (
    <Select className="w-64" value={value} onChange={(e) => onChange(e.target.value)} required>
      <option value="">질문 선택</option>
      {current?.deleted ? <option value={current.id}>{current.query} (삭제됨)</option> : null}
      {live.map((q) => (
        <option key={q.id} value={q.id}>
          {q.query}
        </option>
      ))}
    </Select>
  );
}
